// src/pages/admin/PayoutManagementPage.tsx
import React, { useState, useEffect } from 'react';
import { useUI } from '../../context/UIContext';
import { apiClient } from '../../api/apiClient';
import '../../styles/PayoutManagement.css';

interface Payout {
  _id: string;
  payoutId: string;
  affiliateId: {
    _id: string;
    personalInfo: {
      fullName: string;
      email: string;
    };
    referralCode: string;
  };
  amount: number;
  commissionCount: number;
  method: string;
  payoutDetails: {
    netAmount?: number;
    processingFee?: number;
    transactionId?: string;
    recipientEmail?: string;
    recipientAccount?: string;
  };
  status: string;
  createdAt: string;
  requestedAt?: string;
  processedAt?: string;
  completedAt?: string;
  failureReason?: string;
}

const PayoutManagementPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState<any>({});
  const [filters, setFilters] = useState({
    status: 'all',
    method: 'all'
  });
  const [selectedPayouts, setSelectedPayouts] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchPayouts();
  }, [currentPage, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPayouts = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/v1/admin/payouts', {
        params: {
          page: currentPage,
          limit: 50,
          ...(filters.status !== 'all' && { status: filters.status }),
          ...(filters.method !== 'all' && { method: filters.method }),
        },
      });
      const data = response.data;

      if (data.success) {
        setPayouts(data.data.payouts || []);
        setCurrentPage(data.data.currentPage || currentPage);
        setTotalPages(data.pagination?.pages || 1);
        if (data.stats) {
          setStats(data.stats);
        }
      }
    } catch (error: any) {
      console.error('Fetch payouts error:', error);
      showSnackbar(error?.message || 'An error occurred loading payouts', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessSelected = async () => {
    if (selectedPayouts.length === 0) {
      showSnackbar('Please select at least one payout to process', 'error');
      return;
    }

    try {
      setIsProcessing(true);
      const response = await apiClient.post('/v1/admin/payouts/process', {
        payoutIds: selectedPayouts,
      });
      const data = response.data;

      if (data.success) {
        showSnackbar(data.message || 'Payouts processed successfully', 'success');
        setSelectedPayouts([]);
        fetchPayouts();
      }
    } catch (error: any) {
      console.error('Process payouts error:', error);
      showSnackbar(error?.message || 'An error occurred processing payouts', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectPayout = (payoutId: string) => {
    setSelectedPayouts(prev => 
      prev.includes(payoutId)
        ? prev.filter(id => id !== payoutId)
        : [...prev, payoutId]
    );
  };

  const handleSelectAll = () => {
    const pendingPayouts = payouts.filter(p => p.status === 'pending').map(p => p._id);
    if (selectedPayouts.length === pendingPayouts.length) {
      setSelectedPayouts([]);
    } else {
      setSelectedPayouts(pendingPayouts);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
        return 'badge-success';
      case 'pending':
        return 'badge-warning';
      case 'processing':
        return 'badge-info';
      case 'failed':
        return 'badge-error';
      case 'cancelled':
        return 'badge-default';
      default:
        return 'badge-default';
    }
  };

  const pendingPayouts = payouts.filter(p => p.status === 'pending');
  const allSelected = pendingPayouts.length > 0 && selectedPayouts.length === pendingPayouts.length;

  return (
    <div className="payout-management">
      <div className="payout-management__header">
        <div>
          <h1>Payout Management</h1>
          <p>Process and manage referral agent payout requests</p>
        </div>
        {selectedPayouts.length > 0 && (
          <button
            onClick={handleProcessSelected}
            disabled={isProcessing}
            className="btn-primary"
          >
            {isProcessing ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Processing...
              </>
            ) : (
              <>
                <i className="fas fa-check"></i> Process Selected ({selectedPayouts.length})
              </>
            )}
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="payout-stats">
        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--warning">
            <i className="fas fa-clock"></i>
          </div>
          <div className="stat-card__content">
            <h3>Pending</h3>
            <p className="stat-card__value">{stats.pending?.count || 0}</p>
            <small>{formatCurrency(stats.pending?.totalAmount || 0)}</small>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--info">
            <i className="fas fa-spinner"></i>
          </div>
          <div className="stat-card__content">
            <h3>Processing</h3>
            <p className="stat-card__value">{stats.processing?.count || 0}</p>
            <small>{formatCurrency(stats.processing?.totalAmount || 0)}</small>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--success">
            <i className="fas fa-check-circle"></i>
          </div>
          <div className="stat-card__content">
            <h3>Completed</h3>
            <p className="stat-card__value">{stats.completed?.count || 0}</p>
            <small>{formatCurrency(stats.completed?.totalAmount || 0)}</small>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card__icon stat-card__icon--error">
            <i className="fas fa-times-circle"></i>
          </div>
          <div className="stat-card__content">
            <h3>Failed</h3>
            <p className="stat-card__value">{stats.failed?.count || 0}</p>
            <small>{formatCurrency(stats.failed?.totalAmount || 0)}</small>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="payout-filters">
        <div className="filter-group">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            className="form-input"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Method</label>
          <select
            value={filters.method}
            onChange={(e) => setFilters(prev => ({ ...prev, method: e.target.value }))}
            className="form-input"
          >
            <option value="all">All Methods</option>
            <option value="paypal">PayPal</option>
          </select>
        </div>
        <button onClick={fetchPayouts} className="btn-secondary">
          <i className="fas fa-sync"></i> Refresh
        </button>
      </div>

      {/* Payouts Table */}
      {isLoading ? (
        <div className="payout-management__loading">
          <div className="spinner"></div>
          <p>Loading payouts...</p>
        </div>
      ) : payouts.length === 0 ? (
        <div className="payout-management__empty">
          <i className="fas fa-file-invoice-dollar"></i>
          <p>No payouts found</p>
          <small>No payouts match your current filters</small>
        </div>
      ) : (
        <>
          <div className="payout-management__table-container">
            <table className="payout-management__table">
              <thead>
                <tr>
                  {filters.status === 'pending' && (
                    <th>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        title="Select all pending payouts"
                      />
                    </th>
                  )}
                  <th>Payout ID</th>
                  <th>Referral Agent</th>
                  <th>Amount</th>
                  <th>Net Amount</th>
                  <th>Method</th>
                  <th>Commissions</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((payout) => (
                  <tr key={payout._id}>
                    {filters.status === 'pending' && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedPayouts.includes(payout._id)}
                          onChange={() => handleSelectPayout(payout._id)}
                        />
                      </td>
                    )}
                    <td><code>{payout.payoutId}</code></td>
                    <td>
                      <strong>{payout.affiliateId.personalInfo.fullName}</strong><br />
                      <small>{payout.affiliateId.personalInfo.email}</small>
                    </td>
                    <td className="amount">{formatCurrency(payout.amount)}</td>
                    <td className="amount">{formatCurrency(payout.payoutDetails.netAmount || payout.amount)}</td>
                    <td>
                      <span className="method-badge">
                        {payout.method === 'paypal' && <i className="fab fa-paypal"></i>}
                        {payout.method === 'paypal' ? 'PayPal' : (payout.method?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || payout.method)}
                      </span>
                    </td>
                    <td>{payout.commissionCount}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(payout.status)}`}>
                        {payout.status}
                      </span>
                    </td>
                    <td>{formatDate(payout.requestedAt || payout.createdAt)}</td>
                    <td>
                      {payout.status === 'pending' && (
                        <button
                          onClick={() => {
                            setSelectedPayouts([payout._id]);
                            handleProcessSelected();
                          }}
                          className="btn-action btn-action--success"
                          title="Process this payout"
                        >
                          <i className="fas fa-check"></i> Process
                        </button>
                      )}
                      {payout.status === 'completed' && payout.payoutDetails.transactionId && (
                        <code title={payout.payoutDetails.transactionId}>
                          {payout.payoutDetails.transactionId.substring(0, 12)}...
                        </code>
                      )}
                      {payout.status === 'failed' && payout.failureReason && (
                        <span className="error-text" title={payout.failureReason}>
                          <i className="fas fa-exclamation-triangle"></i>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="payout-management__pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-left"></i> Previous
              </button>
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PayoutManagementPage;
